import {Button, Checkbox, FormControl, IconButton, SelectPanel, Textarea, Truncate, VisuallyHidden} from '@primer/react'
import type {
  ParameterValue,
  RegisteredRuleSchemaComponent,
  RequiredReviewerError,
  RulesetRoutePayload,
  SchemaField,
  ValidationError,
} from '../../types/rules-types'
import {useCallback, useEffect, useMemo, useRef, useState, type FC, type RefObject} from 'react'
import {Blankslate, DataTable, Dialog, Table} from '@primer/react/experimental'
import {AlertFillIcon, InfoIcon, PencilIcon, PeopleIcon, PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {IntegerField} from './builtin/IntegerField'
import {debounce} from '@github/mini-throttle'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {RulesetFormErrorFlash} from '../RulesetFormErrorFlash'

import styles from './RequiredReviewers.module.css'
import {clsx} from 'clsx'

// Data shape for saving the require reviewer data
export type RequiredReviewer = {
  reviewer_id: string
  minimum_approvals: number
  file_patterns: string[]
}

// Metadata returned from the pull request rule
export type PullRequestRuleMetadata = {
  requiredReviewers: Record<string, RequiredReviewerMetadata>
}

// Metadata for each required reviewer
// We may have null values for the metadata if the required reviewer is not found
type RequiredReviewerMetadata = {
  id: number | null
  globalRelayId: string
  type: string | null
  name: string | null
  // Used to determine if the required reviewer is new or not. A new reviewer is added to the top of the list.
  // We only set this value in the UI
  isNew?: boolean
}

export function RequiredReviewers({
  field,
  readOnly,
  value,
  onValueChange,
  metadata,
  errors,
  fieldRef,
}: RegisteredRuleSchemaComponent) {
  const requiredReviewerList = useMemo(() => (value || []) as RequiredReviewer[], [value])
  const {baseAvatarUrl} = useRoutePayload<RulesetRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const suggestionsUrl = resolvePath('required_reviewer_suggestions')

  const [requiredReviewerMetadata, setRequiredReviewerMetadata] = useState<Record<string, RequiredReviewerMetadata>>(
    () => (metadata as PullRequestRuleMetadata)?.requiredReviewers || {},
  )

  // Return the reviewers in the preferred order to be displayed in the table
  const requiredReviewers = useMemo(() => {
    const newReviewers = [] as RequiredReviewer[]
    let orderedByName = [] as RequiredReviewer[]
    const missingReviewers = [] as RequiredReviewer[]

    for (const reviewer of requiredReviewerList) {
      const m = requiredReviewerMetadata[reviewer.reviewer_id]
      if (m && m.isNew) {
        newReviewers.push(reviewer)
      } else if (m && m.id && m.name) {
        const index = orderedByName.findIndex(r => {
          const compareMetadata = requiredReviewerMetadata[r.reviewer_id]
          return (m.name || '').localeCompare(compareMetadata?.name || '') <= 0
        })
        if (index === -1) {
          orderedByName.push(reviewer)
        } else {
          orderedByName = [...orderedByName.slice(0, index), reviewer, ...orderedByName.slice(index)]
        }
      } else {
        missingReviewers.push(reviewer)
      }
    }
    // For ordering, we want the reviewers that were just added while editing this rule to be at the top
    // After, we will surface the reviewers that are missing
    // Finally we will sort the exisiting reviewers by name
    return [...newReviewers, ...missingReviewers, ...orderedByName]
  }, [requiredReviewerMetadata, requiredReviewerList])

  const [ruleChecked, setRuleChecked] = useState<boolean>(requiredReviewers.length > 0)

  const [showDialog, setShowDialog] = useState(false)

  // Used to pass the required reviewer to the dialog for editing
  const [editRequiredReviewer, setEditRequiredReviewer] = useState<RequiredReviewer | undefined>(undefined)

  const onEditRequiredReviewer = (requiredReviewer: RequiredReviewer) => {
    setEditRequiredReviewer(requiredReviewer)
    setShowDialog(true)
  }

  const deleteRequiredReviewer = useCallback(
    (requiredReviewer: RequiredReviewer) => {
      const index = requiredReviewers.findIndex(r => r.reviewer_id === requiredReviewer.reviewer_id)
      const updatedRequiredReviewers: RequiredReviewer[] = [
        ...requiredReviewers.slice(0, index),
        ...requiredReviewers.slice(index + 1),
      ]
      onValueChange(updatedRequiredReviewers)
    },
    [onValueChange, requiredReviewers],
  )

  const addOrUpdateRequiredReviewer = useCallback(
    (requiredReviewer: RequiredReviewer, newMetadata?: RequiredReviewerMetadata) => {
      let updatedRequiredReviewers: RequiredReviewer[]
      if (editRequiredReviewer) {
        // Update required reviewer data used for saving
        const index = requiredReviewers.findIndex(r => r.reviewer_id === editRequiredReviewer.reviewer_id)
        updatedRequiredReviewers = [
          ...requiredReviewers.slice(0, index),
          requiredReviewer,
          ...requiredReviewers.slice(index + 1),
        ]
      } else {
        // If we are creating a new required reviewer, add it to the top of the list for visibility
        updatedRequiredReviewers = [requiredReviewer, ...requiredReviewers]
      }
      if (newMetadata) {
        // Update metadata
        setRequiredReviewerMetadata(previousMetadata => ({
          ...previousMetadata,
          [requiredReviewer.reviewer_id]: newMetadata,
        }))
      }
      onValueChange(updatedRequiredReviewers)
    },
    [editRequiredReviewer, onValueChange, requiredReviewers],
  )

  const onCheckboxChange = (isChecked: boolean) => {
    setRuleChecked(isChecked)
    if (!isChecked) {
      // If the rule is being unchecked, then we need to clear the required reviewers
      // This is to ensure the rule is not saved when it is unchecked
      onValueChange([])
    }
  }

  const firstErrorRef = useRef<string | undefined>(undefined)

  const {errorsByReviewer, genericErrors} = useMemo(() => {
    const ruleErrors = {} as Record<string, string[]>
    const reviewerErrors = {} as Record<string, ValidationError>
    for (const error of (errors[0]?.sub_errors || []) as RequiredReviewerError[]) {
      if (error.reviewer_id !== undefined && error.error_code !== 'reviewer_not_found') {
        // Focus on the first row with an error
        // eslint-disable-next-line react-hooks/refs
        firstErrorRef.current = firstErrorRef.current || error.reviewer_id
        // If there is an error on a specific reviewer, set the error for that reviewer id
        // We can use this to display the error on the appropriate row
        reviewerErrors[error.reviewer_id] = error
      } else {
        // Reset the ref if we have a generic error
        // Instead of focusing on a specific row, we are going to focus on the generic error banner above the table
        // eslint-disable-next-line react-hooks/refs
        firstErrorRef.current = undefined
        ruleErrors[error.error_code] = ruleErrors[error.error_code] || []
        if (!ruleErrors[error.error_code]) {
          ruleErrors[error.error_code] = []
        }
        ruleErrors[error.error_code] = ruleErrors[error.error_code] || []
        ruleErrors[error.error_code]!.push(error.message)
      }
    }
    return {
      errorsByReviewer: reviewerErrors,
      genericErrors: ruleErrors,
    }
  }, [errors])

  const fieldLabel = `${field.name}Label`

  // Start of datatable pagination configuration
  const pageSize = 10
  const [isPaginated, setIsPaginated] = useState(pageSize < requiredReviewers.length)
  const [currentPaginatedPage, setCurrentPaginatedPage] = useState(0)
  const paginationStart = currentPaginatedPage * pageSize
  const paginationEnd = paginationStart + pageSize
  const rows = requiredReviewers.slice(paginationStart, paginationEnd)

  // This useEffect is used to update the pagination state when the required reviewers change
  useEffect(() => {
    // if we are paginated and we were on a page that no longer exists, go back a page
    if (isPaginated && currentPaginatedPage > 0 && currentPaginatedPage * pageSize >= requiredReviewers.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentPaginatedPage(currentPaginatedPage - 1)
    }
    setIsPaginated(pageSize < requiredReviewers.length)

    // Exclude isPaginated because we don't want to rerender the table when the pagination state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredReviewers.length, pageSize, currentPaginatedPage])

  // End of datatable pagination configuration

  // // This useEffect is used for retoggling the checkbox on revert
  useEffect(() => {
    // If he rule is not checked and we have required reviewers, then we need to check the rule
    // This will recheck the rule on revert
    if (!ruleChecked && requiredReviewerList.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRuleChecked(true)
    }
    // Exclude ruleChecked because we don't want to rerender the checkbox when the rule is checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredReviewerList])

  return (
    <>
      {!readOnly ? (
        <>
          <FormControl>
            <Checkbox
              aria-labelledby={fieldLabel}
              checked={ruleChecked}
              onChange={e => {
                onCheckboxChange(e.target.checked)
              }}
            />
            <FormControl.Label id={fieldLabel}>{field.display_name}</FormControl.Label>
            <FormControl.Caption>{field.description}</FormControl.Caption>
          </FormControl>
          {genericErrors && Object.keys(genericErrors).length > 0 && (
            <RulesetFormErrorFlash
              errorId={fieldLabel}
              errorRef={fieldRef as RefObject<HTMLDivElement | null>}
              className={styles.RulesetFormErrorFlash}
            >
              <ul className={styles.Box}>
                {Object.entries(genericErrors).map(([key, values]) => (
                  <li key={key}>{[...new Set(values)].join(', ')}</li>
                ))}
              </ul>
            </RulesetFormErrorFlash>
          )}
        </>
      ) : (
        ruleChecked && (
          <div>
            <span className="d-flex flex-items-center text-bold">{field.display_name}</span>
            <span className="d-block text-small color-fg-muted">{field.description}</span>
          </div>
        )
      )}
      {ruleChecked && (
        <>
          <Table.Container className={readOnly ? styles.tableContainerReadOnly : styles.tableContainerEditable}>
            {!readOnly ? (
              <div className={clsx('Box-header m-0 p-2 d-flex flex-justify-between flex-items-center', styles.Box_1)}>
                <div className="Box-title px-2">{`${requiredReviewers.length} reviewers`}</div>
                <Button leadingVisual={PlusIcon} aria-haspopup="dialog" onClick={() => setShowDialog(true)}>
                  Add reviewer
                </Button>
              </div>
            ) : null}
            {requiredReviewers.length === 0 ? (
              <div className={clsx('Box', styles.Box_2)}>
                <Blankslate>
                  <Blankslate.Heading>No reviewers have been added</Blankslate.Heading>
                </Blankslate>
              </div>
            ) : (
              <DataTable
                data={rows.map(reviewer => ({
                  ...reviewer,
                  actor: requiredReviewerMetadata[reviewer.reviewer_id],
                  id: reviewer.reviewer_id,
                }))}
                cellPadding="condensed"
                columns={[
                  {
                    header: 'Reviewer',
                    field: 'actor',
                    rowHeader: true,
                    maxWidth: '25%',
                    renderCell: row => {
                      return (
                        <div>
                          {row.actor?.id ? (
                            <>
                              <div className={styles.Box_3}>
                                <ActorAvatar actor={row.actor} baseAvatarUrl={baseAvatarUrl} />
                                <span>{row.actor?.name}</span>
                              </div>
                              {errorsByReviewer[row.reviewer_id] && (
                                <span id={`${row.reviewer_id}-error`} className={styles.Text}>
                                  <AlertFillIcon size={12} />
                                  <span>{errorsByReviewer[row.reviewer_id]?.message}</span>
                                </span>
                              )}
                            </>
                          ) : (
                            <span id={`${row.reviewer_id}-error`} className={styles.Text}>
                              <AlertFillIcon size={12} />
                              <span>Reviewer not found</span>
                            </span>
                          )}
                        </div>
                      )
                    },
                  },
                  {
                    header: 'Approvals',
                    field: 'minimum_approvals',
                    width: 'auto',
                    renderCell: row => <span>{row.minimum_approvals}</span>,
                  },
                  {
                    header: 'File patterns',
                    field: 'file_patterns',
                    width: 'grow',
                    maxWidth: '50%',
                    renderCell: row => <FilePatternGroup filePatterns={row.file_patterns} />,
                  },
                  {
                    header: () => <VisuallyHidden>Actions</VisuallyHidden>,
                    id: 'actions',
                    width: 70,
                    renderCell: row => (
                      <IconButton
                        type="button"
                        icon={readOnly ? InfoIcon : PencilIcon}
                        aria-label={`${readOnly ? 'View' : 'Edit'} ${
                          row.actor?.id ? row.actor.name : 'the required reviewer'
                        }'s file patterns`}
                        size="small"
                        variant="invisible"
                        ref={
                          row.reviewer_id === firstErrorRef.current
                            ? (fieldRef as RefObject<HTMLButtonElement | null>)
                            : undefined
                        }
                        onClick={() => onEditRequiredReviewer(row)}
                        className={styles.IconButton}
                      />
                    ),
                  },
                ]}
              />
            )}
            {isPaginated && (
              <Table.Pagination
                aria-label="Pagination for required reviewers"
                pageSize={pageSize}
                totalCount={requiredReviewers.length}
                onChange={pageInfo => {
                  setCurrentPaginatedPage(pageInfo.pageIndex)
                }}
              />
            )}
          </Table.Container>
        </>
      )}
      {showDialog && (
        <ReviewerDialog
          field={field}
          currentRequiredReviewers={requiredReviewers}
          baseAvatarUrl={baseAvatarUrl}
          existingRequiredReviewer={
            editRequiredReviewer
              ? {
                  requiredReviewer: editRequiredReviewer,
                  metadata: requiredReviewerMetadata[editRequiredReviewer.reviewer_id],
                }
              : undefined
          }
          onDelete={deleteRequiredReviewer}
          onSave={addOrUpdateRequiredReviewer}
          onClose={() => {
            setShowDialog(false)
            setEditRequiredReviewer(undefined)
          }}
          suggestionsUrl={suggestionsUrl}
          readOnly={readOnly}
        />
      )}
    </>
  )
}

const FilePatternGroup = ({filePatterns}: {filePatterns: string[]}) => {
  const [showFilePatternOverlay, setShowFilePatternOverlay] = useState<boolean>(false)
  const hiddenFilePatterns = filePatterns.slice(1)
  if (filePatterns.length === 0) {
    return null
  }
  return (
    <>
      <div className="d-flex flex-items-center width-fit">
        <div className={styles.Box_4}>
          <Truncate title={filePatterns[0] as string} maxWidth={200}>
            {filePatterns[0]}
          </Truncate>
        </div>
        {hiddenFilePatterns.length > 0 && (
          <Button
            variant="invisible"
            size="small"
            onClick={() => {
              setShowFilePatternOverlay(true)
            }}
          >
            <VisuallyHidden>Show +{hiddenFilePatterns.length} more</VisuallyHidden>
            <span aria-hidden="true" className="fg-color-muted">
              + {hiddenFilePatterns.length} more
            </span>
          </Button>
        )}
      </div>
      {showFilePatternOverlay && (
        <Dialog
          title="File patterns"
          onClose={() => {
            setShowFilePatternOverlay(false)
          }}
        >
          <div className="d-flex flex-column">
            <pre className="bgColor-inset f5 mt-1 overflow-x-auto tmp-p-3 rounded-2 text-mono">
              {filePatterns.join('\n')}
            </pre>
            <span className={styles.Text_1}>
              Pull requests which change matching files must be approved by the specified team.
            </span>
          </div>
        </Dialog>
      )}
    </>
  )
}

/**
 * ↑↑↑                                                                                   ↑↑↑
 * ↑↑↑ The code above is part of the rule row form as additional options for the PR rule ↑↑↑
 * ↑↑↑                                                                                   ↑↑↑
 *
 * ↓↓↓                                                                    ↓↓↓
 * ↓↓↓ The code below here is all related to the required reviewer dialog ↓↓↓
 * ↓↓↓                                                                    ↓↓↓
 */

type ReviewerDialogProps = {
  field: SchemaField
  currentRequiredReviewers: RequiredReviewer[]
  existingRequiredReviewer?: {requiredReviewer: RequiredReviewer; metadata?: RequiredReviewerMetadata}
  onSave: (requiredReviewer: RequiredReviewer, newMetadata?: RequiredReviewerMetadata) => void
  onDelete: (requiredReviewer: RequiredReviewer) => void
  onClose: () => void
  suggestionsUrl: string
  baseAvatarUrl: string
  readOnly?: boolean
}

const ReviewerDialog: FC<ReviewerDialogProps> = ({
  field,
  currentRequiredReviewers,
  existingRequiredReviewer,
  onDelete,
  onClose,
  onSave,
  suggestionsUrl,
  baseAvatarUrl,
  readOnly,
}) => {
  const {requiredReviewer: existingReviewer, metadata} = existingRequiredReviewer || {}

  // For reviewer select panel
  const [selectedReviewer, setSelectedReviewer] = useState<SuggestionItem | undefined>(() => {
    // Check metadata because it will be `null` if the reviewer is not found
    return existingReviewer && metadata && metadata.id && metadata.name && metadata.type
      ? {
          id: existingReviewer?.reviewer_id,
          dbId: metadata.id,
          type: metadata.type,
          text: metadata.name,
          description: calculateDescription(metadata.name, metadata.type),
          leadingVisual: () => (
            <ActorAvatar
              actor={{id: metadata.id, type: metadata.type, name: metadata.name}}
              baseAvatarUrl={baseAvatarUrl}
            />
          ),
        }
      : undefined
  })
  const [reviewerError, setReviewerError] = useState<string | undefined>(undefined)

  // Approvals dropdown
  const [minimumApprovals, setMinimumApprovals] = useState<number>(existingReviewer?.minimum_approvals || 0)

  // File pattern textbox
  const [filePatterns, setFilePatterns] = useState<string>(existingReviewer?.file_patterns.join('\n') || '')
  const [filePatternsError, setFilePatternsError] = useState<string | undefined>(undefined)
  // Placeholder text for file pattern text box
  const placeholderText = `*
scripts/foo.js
scripts/*
*.js
`
  const filePatternDescription =
    (field.type === 'array' &&
      field.content_object &&
      field.content_object.fields.find(f => f.name === 'file_patterns')?.description) ||
    'File patterns'

  const [reviewerMetadata, setReviewerMetadata] = useState<RequiredReviewerMetadata | undefined>(metadata)

  // This is used to focus on the first error field when the dialog is opened
  const firstErrorField = useRef<HTMLTextAreaElement | HTMLButtonElement | null>(null)
  const selectPanelRef = useRef<HTMLButtonElement | null>(null)
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

  const onDeleteClick = useCallback(() => {
    if (existingReviewer) {
      onDelete(existingReviewer)
    }
    onClose()
  }, [existingReviewer, onClose, onDelete])

  const validateReviewer = useCallback(
    (reviewerIdValue: string | undefined, setErrorField: boolean = false) => {
      // Clear validation errors
      setReviewerError(undefined)
      if (!reviewerIdValue) {
        setReviewerError('A reviewer must be selected')
        if (setErrorField) {
          firstErrorField.current = selectPanelRef.current
        }
      }
      // We don't allow adding (or changing an existing reviwer to) a team that is already added as a reviewer
      if (
        existingReviewer?.reviewer_id !== reviewerIdValue &&
        currentRequiredReviewers.find(r => r.reviewer_id === reviewerIdValue)
      ) {
        setReviewerError('This team is already added as a reviewer')
        if (setErrorField) {
          firstErrorField.current = selectPanelRef.current
        }
      }
    },
    [currentRequiredReviewers, existingReviewer?.reviewer_id],
  )

  const validateValues = useCallback(
    (reviewerIdValue: string | undefined, filePatternValue: string[]) => {
      // Clear validation error
      setFilePatternsError(undefined)
      firstErrorField.current = null
      validateReviewer(reviewerIdValue, true)
      if (filePatternValue.length === 0) {
        setFilePatternsError('At least one file pattern must be specified')
        if (!firstErrorField.current) {
          firstErrorField.current = textAreaRef.current
        }
      }
      firstErrorField.current?.focus()
      firstErrorField.current?.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'})
      // If we have a first error field, return false, which indicates values are not valid
      return !firstErrorField.current
    },
    [validateReviewer],
  )

  const onDone = useCallback(() => {
    const formattedFilePatterns = filePatterns.split('\n').filter(line => line.trim() !== '')
    if (validateValues(selectedReviewer?.id, formattedFilePatterns)) {
      onSave(
        {
          reviewer_id: selectedReviewer?.id,
          minimum_approvals: minimumApprovals,
          file_patterns: formattedFilePatterns,
        } as RequiredReviewer,
        reviewerMetadata,
      )
      onClose()
    }
  }, [filePatterns, minimumApprovals, onClose, onSave, reviewerMetadata, selectedReviewer?.id, validateValues])

  const updateMinimumApprovals = (value: ParameterValue) => {
    setMinimumApprovals(value as number)
  }

  const updateFilePatterns = (value: ParameterValue) => {
    setFilePatternsError(undefined)
    setFilePatterns(value as string)
  }

  const onSelect = (actor: ItemInput | undefined, options: SuggestionItem[]) => {
    let selected: SuggestionItem | undefined = undefined
    if (actor === undefined) {
      // deselect the reviewer
      setSelectedReviewer(undefined)
      setReviewerMetadata(undefined)
    } else {
      selected = options.find(item => item.id === actor?.id)
      if (selected) {
        setSelectedReviewer(selected)
        setReviewerMetadata({
          id: selected.dbId,
          name: selected.text,
          type: selected.type,
          globalRelayId: selected.id,
          isNew: !existingReviewer,
        } as RequiredReviewerMetadata)
      }
    }
    validateReviewer(selected?.id)
  }

  return (
    <Dialog
      aria-label={`${readOnly ? 'Reviewer details' : `${existingRequiredReviewer ? 'Edit' : 'Add'} reviewer`}`}
      title={`${readOnly ? 'Reviewer details' : `${existingRequiredReviewer ? 'Edit' : 'Add'} reviewer`}`}
      renderFooter={() => {
        if (!readOnly) {
          return (
            <Dialog.Footer>
              <div className="d-flex flex-justify-between flex-1">
                {existingRequiredReviewer && (
                  <div>
                    <Button variant="danger" onClick={onDeleteClick}>
                      Delete
                    </Button>
                  </div>
                )}
                <div className="d-flex flex-justify-end flex-1 gap-2">
                  <Button variant="default" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={onDone}>
                    Done
                  </Button>
                </div>
              </div>
            </Dialog.Footer>
          )
        }
      }}
      onClose={onClose}
      width="large"
    >
      {!readOnly ? (
        <FormControl className={styles.FormControl}>
          <FormControl.Label required>Reviewer</FormControl.Label>
          <RequiredReviewersSelectPanel
            ref={selectPanelRef}
            suggestionsUrl={suggestionsUrl}
            baseAvatarUrl={baseAvatarUrl}
            selectedReviewer={selectedReviewer}
            onSelect={onSelect}
          />
          {reviewerError && (
            <FormControl.Validation variant="error" aria-live="polite">
              {reviewerError}
            </FormControl.Validation>
          )}
        </FormControl>
      ) : (
        <div className="d-flex flex-column tmp-mb-3">
          <span className="d-flex flex-items-center text-bold">Reviewer</span>
          <div className={styles.Box_5}>
            {metadata && (
              <ActorAvatar
                actor={{id: metadata?.id, type: metadata?.type, name: metadata?.name}}
                baseAvatarUrl={baseAvatarUrl}
              />
            )}
            <span>{metadata?.name || 'Reviewer was not found'}</span>
          </div>
        </div>
      )}
      {!readOnly ? (
        <div className="tmp-mb-3">
          <IntegerField
            field={{
              type: 'integer',
              name: 'minimumApprovals',
              description: '',
              display_name: 'Approvals',
              required: false,
              allowed_range: '0..10',
              default_value: 0,
              beta: false,
            }}
            value={minimumApprovals}
            onValueChange={updateMinimumApprovals}
            errors={[]}
            sourceType="enterprise"
          />
        </div>
      ) : (
        <div className="d-flex flex-column tmp-mb-3">
          <span className="d-flex flex-items-center text-bold">Approvals</span>
          <span className="mt-1">{minimumApprovals}</span>
        </div>
      )}
      {!readOnly ? (
        <FormControl>
          <FormControl.Label required>File patterns</FormControl.Label>
          <FormControl.Caption>{filePatternDescription}</FormControl.Caption>
          <Textarea
            block
            ref={textAreaRef}
            value={filePatterns}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFilePatterns(e.target.value)}
            placeholder={placeholderText}
            wrap="off"
            className={styles.Textarea}
          />
          {filePatternsError && (
            <FormControl.Validation variant="error" aria-live="polite">
              {filePatternsError}
            </FormControl.Validation>
          )}
        </FormControl>
      ) : (
        <div className="d-flex flex-column">
          <span className="d-flex flex-items-center text-bold">File patterns</span>
          <pre className="bgColor-inset f5 mt-1 overflow-x-auto tmp-p-3 rounded-2 text-mono">{filePatterns}</pre>
          <span className={styles.Text_1}>
            Pull requests which change matching files must be approved by the specified team.
          </span>
        </div>
      )}
    </Dialog>
  )
}

// The expected returned type from the suggestions endpoint
type SuggestionActor = {
  id: number
  name: string
  type: string
  global_relay_id: string
}

const getSuggestions = async (url: string, query: string): Promise<SuggestionActor[]> => {
  const requestUrl = `${url}?q=${encodeURIComponent(query)}`
  const response = await verifiedFetchJSON(requestUrl, {
    method: 'GET',
  })
  if (response.ok) {
    try {
      return await response.json()
    } catch {
      throw new Error('Failed to read response')
    }
  } else {
    throw new Error('Unexpected error')
  }
}

// The shape of the suggestion item used in the select panel
type SuggestionItem = {
  id: string // Uses global relay id since it will be unique across all actors
  dbId: number | null
  text: string
  description: string
  type: string
  disabled?: boolean
  renderItem?: () => React.ReactElement
  leadingVisual: () => React.ReactElement
}

const calculateDescription = (name: string, type: string) => {
  const description = type === 'BusinessTeam' ? 'Enterprise team' : type
  return `${description} • @${name}`
}

const ActorAvatar = ({
  actor,
  baseAvatarUrl,
}: {
  actor?: Pick<RequiredReviewerMetadata, 'id' | 'type' | 'name'>
  baseAvatarUrl: string
}) => {
  if (actor === undefined) {
    return null
  }
  if (actor.type === 'Team' && actor.name) {
    return (
      <GitHubAvatar
        className="flex-shrink-0"
        alt={`${actor.name} Avatar`}
        src={`${baseAvatarUrl}/t/${actor.id}`}
        size={16}
      />
    )
  }
  if (actor.type === 'BusinessTeam') {
    return <PeopleIcon className="color-fg-muted flex-shrink-0" />
  }
  return null
}

type RequiredReviewersSelectPanelProps = {
  suggestionsUrl: string
  baseAvatarUrl: string
  selectedReviewer: SuggestionItem | undefined
  onSelect: (actor: ItemInput | undefined, options: SuggestionItem[]) => void
  ref: React.RefObject<HTMLButtonElement | null>
}

const RequiredReviewersSelectPanel = ({
  ref,
  suggestionsUrl,
  baseAvatarUrl,
  selectedReviewer,
  onSelect,
}: RequiredReviewersSelectPanelProps) => {
  const blockDescription = isFeatureEnabled('rules_required_reviewers_block_description')
  const [suggestions, setSuggestions] = useState<SuggestionActor[]>([])
  const suggestionItems: ItemInput[] = useMemo(() => {
    const mappedSuggestions = suggestions.map(s => ({
      id: s.global_relay_id,
      text: s.name,
      description: calculateDescription(s.name, s.type),
      ...(blockDescription && {descriptionVariant: 'block' as const}),
      leadingVisual: () => <ActorAvatar actor={{id: s.id, type: s.type, name: s.name}} baseAvatarUrl={baseAvatarUrl} />,
    }))
    return mappedSuggestions
  }, [suggestions, baseAvatarUrl, blockDescription])

  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<ItemInput | undefined>(
    selectedReviewer
      ? {
          id: selectedReviewer.id,
          text: selectedReviewer.text,
          description: selectedReviewer.description,
          leadingVisual: selectedReviewer.leadingVisual,
          disabled: selectedReviewer.disabled,
          renderItem: selectedReviewer.renderItem,
        }
      : undefined,
  )

  const debounceSuggestions = useMemo(
    () =>
      debounce(async (newFilter: string) => {
        setIsLoading(true)
        if (newFilter === '') {
          setSuggestions(await getSuggestions(suggestionsUrl, ''))
        } else {
          setSuggestions(await getSuggestions(suggestionsUrl, newFilter))
        }
        setIsLoading(false)
      }, 200),
    [suggestionsUrl, setSuggestions],
  )

  const onSelectChange = (actor: ItemInput | undefined) => {
    onSelect(
      actor,
      suggestions.map(s => ({
        id: s.global_relay_id,
        dbId: s.id,
        type: s.type,
        // Use the same text and description as the suggestion item
        text: s.name,
        description: calculateDescription(s.name, s.type),
        leadingVisual: () => (
          <ActorAvatar actor={{id: s.id, type: s.type, name: s.name}} baseAvatarUrl={baseAvatarUrl} />
        ),
      })),
    )
  }

  const onOpen = async () => {
    if (!open) {
      setOpen(true)
      setIsLoading(true)
      setSuggestions(await getSuggestions(suggestionsUrl, ''))
      setIsLoading(false)
    } else {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (!selectedReviewer) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelected(undefined)
      return
    }
    if (suggestionItems.length > 0) {
      setSelected(suggestionItems.find(item => item.id === selectedReviewer?.id))
    }
  }, [selectedReviewer, suggestionItems])

  return (
    <SelectPanel
      loading={isLoading}
      placeholderText={'Search reviewers'}
      anchorRef={ref}
      renderAnchor={({children, ...anchorProps}) => (
        <Button trailingVisual={TriangleDownIcon} {...anchorProps} aria-haspopup="dialog">
          {children ?? 'Select reviewer'}
        </Button>
      )}
      open={open}
      onOpenChange={onOpen}
      message={
        suggestionItems.length === 0 && !isLoading
          ? {
              variant: 'empty',
              title: 'No teams found',
              body: (
                <div className={styles.Box_6}>
                  <span className={styles.Text_2}>No teams found{filter.length > 0 ? ` for '${filter}'` : ''}</span>
                  <span className={styles.Text_3}>
                    {filter.length > 0 ? 'Adjust your search term to find other teams. ' : ''}
                    Only visible teams can be added as reviewers.
                  </span>
                </div>
              ),
            }
          : undefined
      }
      items={suggestionItems}
      selected={selected}
      onSelectedChange={onSelectChange}
      filterValue={filter}
      onFilterChange={f => {
        setFilter(f)
        debounceSuggestions(f)
      }}
      overlayProps={{
        width: 'medium',
        height: suggestionItems.length > 6 ? 'medium' : 'auto',
      }}
      title="Select reviewer"
    />
  )
}

RequiredReviewersSelectPanel.displayName = 'RequiredReviewersSelectPanel'
